import type { ReactNode } from 'react';
import { cn } from '../../lib/utils.ts';
import { pillToneClass } from './pill-tone.ts';

/**
 * „KI-Vorschlag · 92 %“: Sicherheit als ganze Prozentzahl (0–100). Ohne gelieferte Sicherheit (`undefined`) nur
 * „KI-Vorschlag“ – nie eine erfundene Zahl (Ticket 1.272).
 */
export function aiConfidenceLabel(confidence?: number): string {
  if (confidence === undefined) return 'KI-Vorschlag';
  const percent = Number.isFinite(confidence) ? Math.round(Math.min(100, Math.max(0, confidence))) : 0;
  return `KI-Vorschlag · ${percent} %`;
}

/**
 * KI-Karte (DESIGN „Seitenaufbau“ › KI-Karte, Ticket 1.271; [RUDOLF 24.10.2026]): KI-Vorschlag auf `s3-blue-50` mit 1 px
 * Kante `s3-blue-300`, Radius `tile`, Innenabstand 12/16; ✦ funkelt einmal beim Erscheinen, Pille „KI-Vorschlag · 92 %“ (ohne gelieferte
 * Sicherheit nur „KI-Vorschlag“)
 * (Schrift `s3-blue-600`, dunkel `s3-link`; Rand `s3-blue-300`). Nach dem Übernehmen (`state="accepted"`) wechselt die
 * Karte in 200 ms auf `ready-bg` mit ✓ und „Übernommen“. Nur Darstellung: ein KI-Ergebnis ist immer Entwurf oder
 * Vorschlag (AD-16) – was „Übernehmen“ auslöst, entscheidet die Seite über einen Command.
 */
export function AiCard({
  title,
  confidence,
  pill,
  meta,
  actions,
  state = 'open',
  acceptedText = 'Übernommen',
  testId,
  stackActions = false,
  actionsBelow = false,
  children,
}: {
  title: ReactNode;
  /** Sicherheit 0–100; weglassen, wenn die Quelle keine liefert (Pille dann nur „KI-Vorschlag“). */
  confidence?: number | undefined;
  /**
   * Inhalt der Pille statt „KI-Vorschlag …“ – nur für Vorschläge, die nicht von einem KI-Modell stammen (z. B. der
   * Adressdienst: „Vorschlag“, Ticket 1.272). `null` = keine Pille (Story 5.121: in einer Liste steht sie einmal oben).
   */
  pill?: ReactNode;
  /** Begründung bzw. Herkunft des Vorschlags. */
  meta?: ReactNode;
  /** Knöpfe („Übernehmen“, „Verwerfen“); nur solange offen. */
  actions?: ReactNode;
  state?: 'open' | 'accepted';
  acceptedText?: string;
  testId?: string | undefined;
  /**
   * Knöpfe unter dem Text statt daneben (Story 5.121, Plan-Änderung 27.10.2026 Punkt 3: KI-Karten am Handy mit Knöpfen
   * unter dem Text). Standard `false` (bisheriges Verhalten).
   */
  stackActions?: boolean;
  /** Opt-in (Ticket 1.274 Teil 2): Knöpfe in eigener Zeile unter dem Inhalt (schmale Spalte), Standard rechts daneben. */
  actionsBelow?: boolean;
  /** Inhalt unter Titel und Meta, z. B. die gelesenen Felder einer Ergebnis-Vorschau (Ticket 1.301); nur solange offen. */
  children?: ReactNode;
}) {
  const accepted = state === 'accepted';
  return (
    <div
      data-ai-card=""
      data-state={state}
      data-testid={testId}
      className={cn(
        'flex min-w-0 flex-wrap items-start gap-space-3 rounded-[var(--radius-tile-lg)] border border-transparent p-space-4 transition-[background-color,border-color] duration-200',
        accepted ? 'bg-ready-bg' : 'bg-surface-card',
      )}
    >
      {/* Redesign 31.10.2026 (Variante C): ruhige graue Karte, der Funke in einem kleinen Kreis in Dunkelblau. */}
      <span aria-hidden className={cn('grid size-8 flex-none place-items-center rounded-full text-body', accepted ? 'text-ready' : 'animate-sparkle bg-card text-brand shadow-ask')}>
        {accepted ? '✓' : '✦'}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-space-1 pt-space-1">
        <div className="flex min-w-0 flex-wrap items-center gap-space-2">
          <p className="m-0 min-w-0 text-row font-medium text-ink">{title}</p>
          {accepted || pill === null ? null : (
            <span className={cn('inline-flex h-pill-h flex-none items-center rounded-pill px-space-2 text-pill', pillToneClass('info'))} data-ai-pill="">
              {pill ?? aiConfidenceLabel(confidence)}
            </span>
          )}
        </div>
        {!accepted && meta ? <p className="m-0 text-body text-ink-2">{meta}</p> : null}
        {!accepted && children ? <div className="mt-space-2 min-w-0">{children}</div> : null}
        {/* Lebende Region von Anfang an im DOM; nur ihr Text wechselt, damit Hilfsmittel „Übernommen“ ansagen. */}
        <p className={cn('m-0 text-body font-medium text-ready', !accepted && 'sr-only')} role="status" aria-live="polite">
          {accepted ? acceptedText : ''}
        </p>
      </div>
      {!accepted && actions ? (
        <div
          className={cn('flex max-w-full min-w-0 flex-none flex-wrap items-center gap-space-2', stackActions && 'basis-full pl-space-6', actionsBelow && 'basis-full justify-end')}
          data-stacked={stackActions ? '' : undefined}
        >
          {actions}
        </div>
      ) : null}
    </div>
  );
}
